import { useEffect, useRef } from 'react';

// Slow-moving topographic contour lines over a starfield, drawn with raw
// WebGL2 (no three.js). Lines shift from indigo to gold and bend gently
// around the cursor. Falls back to the CSS background if WebGL2 is missing.

const VERTEX = `#version 300 es
in vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAGMENT = `#version 300 es
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform vec2 uMouse;
out vec4 outColor;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x),
             mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = m * p; a *= 0.5; }
  return v;
}

void main() {
  float s = min(uRes.x, uRes.y);
  vec2 uv = (gl_FragCoord.xy - 0.5 * uRes) / s;
  vec2 m = (uMouse - 0.5 * uRes) / s;
  float t = uTime;

  // cursor pushes the terrain outward a little
  float md = length(uv - m);
  vec2 p = uv * 1.3 + (uv - m) * 0.35 * exp(-md * md * 7.0);

  // domain-warped field -> contour lines
  vec2 q = vec2(fbm(p + vec2(0.0, t * 0.035)), fbm(p + vec2(5.2, 1.3) - t * 0.03));
  float f = fbm(p + 1.8 * q + t * 0.015);
  float k = f * 16.0;
  float d = abs(fract(k + 0.5) - 0.5);
  float w = fwidth(k);
  float line = 1.0 - smoothstep(0.0, w * 1.3, d);
  float glow = (1.0 - smoothstep(0.0, w * 7.0, d)) * 0.22;

  vec3 indigo = vec3(0.38, 0.43, 0.98);
  vec3 gold = vec3(0.93, 0.79, 0.53);
  vec3 tint = mix(indigo, gold, smoothstep(0.32, 0.62, q.x));

  // quiet in the middle where the text sits, livelier toward the edges
  float edge = smoothstep(0.12, 0.95, length(uv * vec2(0.85, 1.0)));
  float strength = mix(0.12, 0.55, edge);

  vec3 col = vec3(0.016, 0.016, 0.03) + tint * (line * 0.55 + glow) * strength;

  // sparse twinkling stars
  vec2 sp = uv * 55.0;
  vec2 id = floor(sp);
  float h = hash(id);
  if (h > 0.982) {
    vec2 c = fract(sp) - 0.5 - (vec2(hash(id + 1.7), hash(id + 3.1)) - 0.5) * 0.6;
    float star = 1.0 - smoothstep(0.0, 0.09, length(c));
    float tw = 0.45 + 0.55 * sin(t * 1.3 + h * 300.0);
    col += star * tw * 0.75;
  }

  outColor = vec4(col, 1.0);
}
`;

function compile(gl, type, src) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn(gl.getShaderInfoLog(shader));
    return null;
  }
  return shader;
}

function ShaderBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false });
    if (!gl) return;

    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    if (!vs || !fs) return;
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(program, 'uRes');
    const uTime = gl.getUniformLocation(program, 'uTime');
    const uMouse = gl.getUniformLocation(program, 'uMouse');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // phones: the shader runs on every pixel, so draw it at a lower resolution
    // and 30fps. The lines are soft enough that the upscale isn't visible.
    const isPhone = window.matchMedia('(pointer: coarse), (max-width: 600px)').matches;
    const dpr = isPhone ? 0.75 : Math.min(window.devicePixelRatio || 1, 1.5);
    const frameMs = isPhone ? 1000 / 30 : 0;
    // mouse eases toward its target so the bend follows smoothly
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    let raf = 0;
    let time = 20;
    let last = performance.now();

    function resize() {
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      if (!mouse.tx && !mouse.ty) {
        // park the bump off-screen until the cursor shows up
        mouse.x = mouse.tx = -canvas.width;
        mouse.y = mouse.ty = -canvas.height;
      }
      if (reduceMotion) draw();
    }

    function draw() {
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;
      gl.uniform1f(uTime, time);
      gl.uniform2f(uMouse, mouse.x, mouse.y);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function frame(now) {
      if (now - last < frameMs - 2) {
        raf = requestAnimationFrame(frame);
        return;
      }
      time += Math.min(now - last, 50) / 1000;
      last = now;
      draw();
      raf = requestAnimationFrame(frame);
    }

    function onMove(e) {
      mouse.tx = e.clientX * dpr;
      mouse.ty = (window.innerHeight - e.clientY) * dpr;
    }

    function onVisibility() {
      cancelAnimationFrame(raf);
      if (!document.hidden && !reduceMotion) {
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    }

    resize();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);
    if (reduceMotion) {
      draw();
    } else {
      window.addEventListener('pointermove', onMove);
      raf = requestAnimationFrame(frame);
    }
    canvas.classList.add('ready');

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVisibility);
      gl.deleteProgram(program);
      gl.deleteBuffer(buffer);
    };
  }, []);

  return <canvas ref={canvasRef} className="shader-bg" aria-hidden="true" />;
}

export default ShaderBackground;
